import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import * as Styled from './InfoReportByDay.styles';
import { getFromLocalStorage } from '../../global/helpers';
import { UserRole } from '../../containers/OrdersPage/types';
import {
  ReportTabOption,
  SourcesReportResponse,
} from '../../containers/ReportPage/types';
import bottomArrow from '../../global/media/bottom-arrow.svg';
import { getCities } from '../../global/cities';
import { amountOfOrders } from '../../containers/OrdersPage/helpers';
import PaymentSection from './PaymentSection';
import { selectors as userSelectors } from '../../containers/LoginPage/reducer';

interface InfoParam {
  day: string;
  data: any;
}

const InfoReportByDay = ({ data, day }: InfoParam) => {
  const { t } = useTranslation();
  const userRole = getFromLocalStorage('role');
  const userCity = getFromLocalStorage('city') || 'Алматы';
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const [currentTab, setCurrentTab] = useState(ReportTabOption.sales);
  const dataUser = useSelector(userSelectors.userData);

  const isLoyaltyTagEnabled = useMemo(
    () =>
      dataUser?.sourceTags?.some((tag: any) =>
        process.env.REACT_APP_DEV_AREA
          ? tag.tag_id === 1043 || tag.tag_id === 1044
          : tag.tag_id === 1154 || tag.tag_id === 1155
      ),
    [dataUser]
  );

  const {
    closedAmountOfOrdersInPlace: amountsInPlace,
    closedAmountOfOrdersOnline: amountsOnline,
    closedAmountOfOrdersInterpay: amountInterpay,
    closedAmountOfOrdersBeeline: amountBeeline,
    closedAmountOfOrdersEmdel: amountEmdel,
    closedAmountOfOrdersLoyalty: amountLoyalty,
    closedAmountOfOrdersLoyaltyMb: amountLoyaltyMb,
    closedOrdersInPlace: ordersInPlace,
    closedOrdersOnline: ordersOnline,
    closedOrdersInterpay: ordersInterpay,
    closedOrdersBeeline: ordersBeeline,
    closedOrdersEmdel: ordersEmdel,
    closedOrdersLoyalty: ordersLoyalty,
    closedOrdersLoyaltyMb: ordersLoyaltyMb,
    webMobile,
    webDesktop,
    appAndroid,
    appIOS,
    operator,
  } = amountOfOrders(data, currentTab === ReportTabOption.refunds && true);

  const checkRefunds = (refunds: any) => {
    return refunds.some((ref: any) => ref.refund_total);
  };

  const getCountByPaymentPlace = (data: SourcesReportResponse[]) => {
    return data?.reduce((accum, source) => {
      return accum + source.orders.length;
    }, 0);
  };

  const getPriceByCity = (data: SourcesReportResponse[], city: string) => {
    const refunds = currentTab === ReportTabOption.refunds;
    return data
      ?.filter((el) => el.source_city === city)
      ?.reduce((accum, source) => {
        return accum + (refunds ? source.refund_total || 0 : source.total);
      }, 0);
  };

  const getPriceByCityAndPayment = (data: SourcesReportResponse[]) => {
    const refunds = currentTab === ReportTabOption.refunds;
    return data?.reduce((accum, source) => {
      return accum + (refunds ? source.refund_total || 0 : source.total);
    }, 0);
  };

  const getCityOrdersByCity = (data: SourcesReportResponse[], city: string) => {
    return data?.filter((el: any) => el.source_city === city);
  };

  const userSources = [
    {
      title: t('OrdersPage.WEB_DESKTOP'),
      count: getCountByPaymentPlace(webDesktop || []),
      visible: Boolean(getCountByPaymentPlace(webDesktop || [])),
    },
    {
      title: t('OrdersPage.WEB_MOBILE'),
      count: getCountByPaymentPlace(webMobile || []),
      visible: Boolean(getCountByPaymentPlace(webMobile || [])),
    },

    {
      title: t('OrdersPage.APP_ANDROID'),
      count: getCountByPaymentPlace(appAndroid || []),
      visible: Boolean(getCountByPaymentPlace(appAndroid || [])),
    },
    {
      title: t('OrdersPage.APP_IOS'),
      count: getCountByPaymentPlace(appIOS || []),
      visible: Boolean(getCountByPaymentPlace(appIOS || [])),
    },
    {
      title: t('OrdersPage.OPERATOR'),
      count: getCountByPaymentPlace(operator || []),
      visible: Boolean(getCountByPaymentPlace(operator || [])),
    },
  ];

  const isPharmacist = userRole === UserRole.PHARMACIST;
  // Билайн показывается отдельной секцией для админа и оператора.
  // Для этих ролей билайн-заказы НЕ сворачиваем в эквайринг, иначе один
  // заказ учитывается дважды (и в «Эквайринге», и в «Билайне»).
  const hasSeparateBeelineSection =
    userRole === UserRole.ADMIN || userRole === UserRole.OPERATOR;
  const visibleCities = isPharmacist
    ? getCities().filter((f: any) => f.ruName === userCity)
    : getCities();

  const cityPrice = visibleCities.map((f: any) => ({
    title: f.ruName,
    price: `${getPriceByCity(ordersOnline.concat(ordersInPlace), f.ruName)} ₸`,
    visible: Boolean(
      getPriceByCity(ordersOnline.concat(ordersInPlace), f.ruName)
    ),
  }));

  const cityOrders = visibleCities.map((f: any) => ({
    title: f.ruName,
    cityOrdersOnline: getCityOrdersByCity(ordersOnline, f.ruName),
    cityOrdersInPlace: getCityOrdersByCity(ordersInPlace, f.ruName),
    cityOrdersInterpay: getCityOrdersByCity(
      hasSeparateBeelineSection
        ? ordersInterpay
        : [...ordersBeeline, ...ordersInterpay],
      f.ruName
    ),
    cityOrdersBeeline: getCityOrdersByCity(ordersBeeline, f.ruName),
    cityOrdersEmdel: getCityOrdersByCity(ordersEmdel, f.ruName),
    cityOrdersLoyalty: getCityOrdersByCity(ordersLoyalty, f.ruName),
    cityOrdersLoyaltyMb: getCityOrdersByCity(ordersLoyaltyMb, f.ruName),
  }));

  // Чтобы добавить новый тип оплаты нужно добавить объект в массив paymentSections ниже.
  const paymentSections = [
    // {
    //   key: 'kaspi',
    //   title: t('OrdersPage.PAY_KASPI'),
    //   amount: amountsOnline,
    //   quantityKey: 'ALL_QUANTITY_KASPI',
    //   quantityRefundKey: 'ALL_REFUNDS_KASPI',
    //   cityOrders: cityOrders.map(
    //     (el: { title: any; cityOrdersOnline: any }) => ({
    //       title: el.title,
    //       orders: el.cityOrdersOnline,
    //     })
    //   ),
    //   visible: userRole !== UserRole.DISPATCHER_B2B,
    // },
    {
      key: 'interpay',
      title: t('OrdersPage.INTERPAY'),
      amount: hasSeparateBeelineSection
        ? amountInterpay
        : `${
            Number(amountBeeline.split('₸')[0]) +
            Number(amountInterpay.split('₸')[0])
          } `,
      quantityKey: 'ALL_QUANTITY_INTERPAY',
      quantityRefundKey: 'ALL_REFUNDS_INTERPAY',
      cityOrders: cityOrders.map(
        (el: { title: any; cityOrdersInterpay: any }) => ({
          title: el.title,
          orders: el.cityOrdersInterpay,
        })
      ),
      visible: userRole !== UserRole.DISPATCHER_B2B,
    },
    {
      key: 'inplace',
      title: t('OrdersPage.PAY_IN_PLACE'),
      amount: amountsInPlace,
      quantityKey: 'ALL_QUANTITY_PLACE',
      quantityRefundKey: 'ALL_QUANTITY_PLACE',
      cityOrders: cityOrders.map(
        (el: { title: any; cityOrdersInPlace: any }) => ({
          title: el.title,
          orders: el.cityOrdersInPlace,
        })
      ),
      visible:
        userRole !== UserRole.DISPATCHER_B2B &&
        currentTab === ReportTabOption.sales,
    },
    {
      key: 'beeline',
      title: t('OrdersPage.BEELINE'),
      amount: amountBeeline,
      quantityKey: 'ALL_QUANTITY_BEELINE',
      quantityRefundKey: 'ALL_REFUNDS_BEELINE',
      cityOrders: cityOrders.map(
        (el: { title: any; cityOrdersBeeline: any }) => ({
          title: el.title,
          orders: el.cityOrdersBeeline,
        })
      ),
      visible: hasSeparateBeelineSection,
    },
    {
      key: 'emdel',
      title: t('OrdersPage.EMDEL'),
      amount: amountEmdel,
      quantityKey: 'ALL_QUANTITY_EMDEL',
      quantityRefundKey: 'ALL_REFUNDS_EMDEL',
      cityOrders: cityOrders.map(
        (el: { title: any; cityOrdersEmdel: any }) => ({
          title: el.title,
          orders: el.cityOrdersEmdel,
        })
      ),
      visible: userRole !== UserRole.OPERATOR,
    },
    {
      key: 'loyalty',
      title: t('OrdersPage.LOYALTY'),
      amount: amountLoyalty,
      quantityKey: 'ALL_QUANTITY_LOYALTY',
      quantityRefundKey: 'ALL_REFUNDS_LOYALTY',
      cityOrders: cityOrders.map(
        (el: { title: any; cityOrdersLoyalty: any }) => ({
          title: el.title,
          orders: el.cityOrdersLoyalty,
        })
      ),
      visible: userRole === UserRole.PHARMACIST && isLoyaltyTagEnabled,
    },
    {
      key: 'loyalty_mb',
      title: t('OrdersPage.LOYALTY_MB'),
      amount: amountLoyaltyMb,
      quantityKey: 'ALL_QUANTITY_LOYALTY_MB',
      quantityRefundKey: 'ALL_REFUNDS_LOYALTY_MB',
      cityOrders: cityOrders.map(
        (el: { title: any; cityOrdersLoyaltyMb: any }) => ({
          title: el.title,
          orders: el.cityOrdersLoyaltyMb,
        })
      ),
      visible: userRole === UserRole.PHARMACIST && isLoyaltyTagEnabled,
    },
  ];

  const sourceBlockClassname =
    userSources.find((el) => el.visible) && 'source-block';

  return (
    <Styled.Container>
      <Styled.Header
        onClick={() => (isMobile ? setIsCollapsed(!isCollapsed) : null)}
      >
        <Styled.TitleInfo>{day}</Styled.TitleInfo>
        <Styled.ReportTabsContainer>
          <div
            className={`tab-item ${
              currentTab === ReportTabOption.sales && 'type-active'
            }`}
            onClick={() => setCurrentTab(ReportTabOption.sales)}
          >
            {t('ReportPage.TABS.SALES')}
          </div>
          <div
            className={`tab-item ${
              currentTab === ReportTabOption.refunds && 'type-active'
            }`}
            onClick={() => setCurrentTab(ReportTabOption.refunds)}
          >
            {t('ReportPage.TABS.REFUNDS')}
            {(checkRefunds(ordersInterpay) || checkRefunds(ordersOnline)) &&
              currentTab !== ReportTabOption.refunds && (
                <span className="reportChecker" />
              )}
          </div>
        </Styled.ReportTabsContainer>
        {isMobile && (
          <Styled.ImgBlock>
            <img
              className={`headerIcon ${isCollapsed && 'headerIconCollapsed'}`}
              width={16}
              src={bottomArrow}
              alt="bottom arrow"
            />
          </Styled.ImgBlock>
        )}
      </Styled.Header>

      {(!isMobile || (isMobile && !isCollapsed)) && (
        <>
          {(userRole === UserRole.ADMIN ||
            userRole === UserRole.OPERATOR ||
            userRole === UserRole.HEAD_OPERATOR) && (
            <div className={sourceBlockClassname}>
              {userSources.map(
                (el) =>
                  el.visible && (
                    <div key={el.title}>
                      <span className="source-title">{el.title}:</span>
                      <span className="source-count">{el.count}</span>
                    </div>
                  )
              )}
            </div>
          )}

          {(userRole === UserRole.ADMIN ||
            userRole === UserRole.OPERATOR ||
            userRole === UserRole.HEAD_OPERATOR) && (
            <div className="city-price-day">
              {cityPrice.map(
                (el: any) =>
                  el.visible && (
                    <div key={el.title}>
                      <span className="city-title">{el.title} </span>
                      <span className="city-price">{el.price}</span>
                    </div>
                  )
              )}
            </div>
          )}

          <div className="amount-container">
            {paymentSections.map(
              (section) =>
                section.visible && (
                  <PaymentSection
                    key={section.key}
                    title={section.title}
                    amount={section.amount}
                    quantityKey={section.quantityKey}
                    quantityRefundKey={section.quantityRefundKey}
                    currentTab={currentTab}
                    cityOrders={section.cityOrders}
                    getCountByPaymentPlace={getCountByPaymentPlace}
                    getPriceByCityAndPayment={getPriceByCityAndPayment}
                  />
                )
            )}
          </div>
        </>
      )}
    </Styled.Container>
  );
};

export default InfoReportByDay;
